import { CloudOff, Cloud, RefreshCw, AlertTriangle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { getSyncSnapshot, processPending } from '../../services/sync';
import type { SyncSnapshot } from '../../services/sync';

const initial: SyncSnapshot = { status: navigator.onLine ? 'ONLINE' : 'OFFLINE', pendingCount: 0 };

export function ConnectionStatus() {
  const [snapshot, setSnapshot] = useState<SyncSnapshot>(initial);

  useEffect(() => {
    let active = true;
    const load = async () => {
      const next = await getSyncSnapshot();
      if (active) setSnapshot(next);
    };
    const refresh = () => void load();
    void load();
    window.addEventListener('online', refresh);
    window.addEventListener('offline', refresh);
    window.addEventListener('babs:sync-changed', refresh);
    const interval = window.setInterval(refresh, 5000);
    return () => {
      active = false;
      window.removeEventListener('online', refresh);
      window.removeEventListener('offline', refresh);
      window.removeEventListener('babs:sync-changed', refresh);
      window.clearInterval(interval);
    };
  }, []);

  const labels = {
    ONLINE: 'Online',
    OFFLINE: 'Offline',
    SYNCING: 'Syncing…',
    SYNC_ERROR: 'Sync error',
  } as const;
  const Icon =
    snapshot.status === 'OFFLINE'
      ? CloudOff
      : snapshot.status === 'SYNC_ERROR'
        ? AlertTriangle
        : snapshot.status === 'SYNCING'
          ? RefreshCw
          : Cloud;
  return (
    <button
      type="button"
      onClick={() => void processPending()}
      aria-label={`Synchronization status: ${labels[snapshot.status]}`}
      title={snapshot.lastError ?? labels[snapshot.status]}
      className="flex min-h-[52px] min-w-[52px] items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 text-left sm:justify-start"
    >
      <span
        className={`h-2.5 w-2.5 rounded-full ${snapshot.status === 'ONLINE' ? 'bg-emerald-500' : snapshot.status === 'SYNC_ERROR' ? 'bg-red-500' : 'bg-amber-500'}`}
      />
      <Icon
        size={17}
        aria-hidden="true"
        className={snapshot.status === 'SYNCING' ? 'animate-spin' : undefined}
      />
      <div className="hidden leading-tight sm:block">
        <p className="text-xs font-semibold text-slate-700">{labels[snapshot.status]}</p>
        <p className="text-[10px] font-medium text-slate-400">
          {snapshot.pendingCount > 0 ? `${snapshot.pendingCount} waiting to sync` : 'Tap to sync'}
        </p>
      </div>
    </button>
  );
}
